/**
 * BatchPaymentModal — Baixa em lote (Repasse / PIX em lote).
 * Wireframe: settlement/docs/po/baixas-e-liquidacoes-cenarios.xlsx (aba Cessão, "Repasse PIX em lote")
 * Mesma lógica do PIX individual, UX permite baixar múltiplas duplicatas de uma vez.
 */
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { registerBatchPayments } from '../services/settlementApi';
import { PaymentMethod, SettlementType } from '../constants/settlement';
import ReceivingBankAccountSelect from '../../../molecules/payment/ReceivingBankAccountSelect';
import { toast } from 'react-toastify';

interface Props {
    titleDebts: any[];
    onClose: () => void;
    onSuccess: () => void;
}

const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value || '0') : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const BatchPaymentModal: React.FC<Props> = ({ titleDebts, onClose, onSuccess }) => {
    const totalRemaining = titleDebts.reduce((s, td) => s + (parseFloat(td.amount) - parseFloat(td.paid_amount || '0')), 0);

    const [form, setForm] = useState({
        payment_method: PaymentMethod.PIX as string,
        settlement_type: SettlementType.TOTAL as string,
        bank_account: '',  // alias da conta de recebimento — escolhido pelo usuário no select
        payment_source: 'SACADO',
        notes: '',
    });
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
        // o select nasce vazio: sem isto, a baixa em lote sairia sem conta de recebimento
        if (!form.bank_account) {
            toast.error('Selecione a conta de recebimento');
            return;
        }
        setSubmitting(true);
        try {
            const payments = titleDebts.map(td => ({
                title_debt_id: td.id,
                amount: parseFloat(td.amount) - parseFloat(td.paid_amount || '0'),
                payment_method: form.payment_method,
                settlement_type: form.settlement_type,
                bank_account: form.bank_account,
                receiving_bank_account_id: form.bank_account,
                payment_source: form.payment_source,
                notes: form.notes,
            }));
            await registerBatchPayments(payments);
            toast.success(`${titleDebts.length} baixa(s) registrada(s) com sucesso`);
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro: ${e.message}`);
        }
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Baixa em Lote — {titleDebts.length} título(s)
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                {/* Summary */}
                <div className="mb-4 bg-gray-50 dark:bg-slate-700 p-3 rounded text-sm">
                    <p><strong>Total a baixar:</strong> {formatCurrency(totalRemaining)}</p>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">Cada título será baixado pelo seu saldo restante individual.</p>
                </div>

                {/* Selected titles */}
                <div className="mb-4 max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-gray-100 dark:bg-slate-700 sticky top-0">
                            <tr>
                                <th className="py-1 px-2 text-left">Título</th>
                                <th className="py-1 px-2 text-right">Saldo</th>
                                <th className="py-1 px-2">Vencimento</th>
                            </tr>
                        </thead>
                        <tbody>
                            {titleDebts.map(td => (
                                <tr key={td.id} className="border-t dark:border-slate-600">
                                    <td className="py-1 px-2">{td.document_title || `Parcela ${td.installment_number}`}</td>
                                    <td className="py-1 px-2 text-right">{formatCurrency(parseFloat(td.amount) - parseFloat(td.paid_amount || '0'))}</td>
                                    <td className="py-1 px-2">{td.due_date?.split('-').reverse().join('/') || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Form (shared config for all) */}
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meio</label>
                            <select className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                                value={form.payment_method} onChange={e => setForm({ ...form, payment_method: e.target.value })}>
                                <option value={PaymentMethod.PIX}>PIX</option>
                                <option value={PaymentMethod.TED}>TED</option>
                                <option value={PaymentMethod.BOLETO}>Boleto</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Conta de recebimento</label>
                            <ReceivingBankAccountSelect
                                value={form.bank_account}
                                onChange={v => setForm({ ...form, bank_account: v })}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                        <input type="text" className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white"
                            value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                            placeholder="Ex: Repasse lote 09/06..." />
                    </div>
                </div>

                <div className="mt-5 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                        {submitting ? 'Processando...' : `Confirmar ${titleDebts.length} Baixa(s)`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BatchPaymentModal;
